/**
 * Mixin to determine current breakpoint
 * from https://gist.github.com/cb109/b074a65f7595cffc21cea59ce8d15f9b
 *
 *  Use within a component:
 *   import breakpoint from './breakpoint.js';
 *
 *   export default {
 *     name: 'my-component',
 *     mixins: [breakpoint],
 *     ...
 *
 * Then inside a template:
 *   <div v-if="$breakpoint.smAndDown">...</div>
 *
 * @type {{destroyed(): void, data(): {clientWidth: number, clientHeight: number}, computed: {$breakpoint(): {smOnly: boolean|boolean, mdOnly: boolean|boolean, mdAndDown: boolean, mdAndUp: boolean|boolean, smAndUp: boolean|boolean, xl: boolean|boolean, smAndDown: boolean, md: boolean|boolean, name: *, xsOnly: boolean, width: breakpoint.computed.clientWidth, sm: boolean|boolean, xs: boolean, lg: boolean|boolean, lgOnly: boolean|boolean, lgAndUp: boolean|boolean, lgAndDown: boolean, xlOnly: boolean|boolean, height: breakpoint.computed.clientHeight}}, methods: {_updateDimensions(): void}, mounted(): void}}
 */

export const breakpoint = {
  data() {
    return {
      clientWidth: 0,
      clientHeight: 0
    };
  },
  computed: {
    $breakpoint() {
      let xs = this.clientWidth < 320;
      let sm = this.clientWidth < 768 && !xs;
      let md = this.clientWidth < 1024 && !(sm || xs);
      let lg = this.clientWidth < 1440 && !(md || sm || xs);
      let xl = this.clientWidth >= 1441 && !(lg || md || sm || xs);

      let xsOnly = xs;
      let smOnly = sm;
      let smAndDown = (xs || sm) && !(md || lg || xl);
      let smAndUp = !xs && (sm || md || lg || xl);
      let mdOnly = md;
      let mdAndDown = (xs || sm || md) && !(lg || xl);
      let mdAndUp = !(xs || sm) && (md || lg || xl);
      let lgOnly = lg;
      let lgAndDown = (xs || sm || md || lg) && !xl;
      let lgAndUp = !(xs || sm || md) && (lg || xl);
      let xlOnly = xl;

      let name;
      switch (true) {
        case xs:
          name = "xs";
          break;
        case sm:
          name = "sm";
          break;
        case md:
          name = "md";
          break;
        case lg:
          name = "lg";
          break;
        default:
          name = "xl";
          break;
      }

      return {
        // Definite breakpoint.
        xs: xs,
        sm: sm,
        md: md,
        lg: lg,
        xl: xl,

        // Useful e.g. to construct CSS class names dynamically.
        name: name,

        // Breakpoint ranges.
        xsOnly: xsOnly,
        smOnly: smOnly,
        smAndDown: smAndDown,
        smAndUp: smAndUp,
        mdOnly: mdOnly,
        mdAndDown: mdAndDown,
        mdAndUp: mdAndUp,
        lgOnly: lgOnly,
        lgAndDown: lgAndDown,
        lgAndUp: lgAndUp,
        xlOnly: xlOnly,

        // For custom breakpoint logic.
        width: this.clientWidth,
        height: this.clientHeight
      };
    }
  },
  methods: {
    _updateDimensions() {
      // Cross-browser support as described in:
      // https://stackoverflow.com/questions/1248081
      this.clientWidth = Math.max(
        document.documentElement.clientWidth,
        window.innerWidth || 0
      );
      this.clientHeight = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight || 0
      );
    }
  },
  mounted() {
    this.$nextTick(() => {
      this._updateDimensions();
      window.addEventListener("resize", this._updateDimensions, {
        passive: true
      });
    });
  },
  destroyed() {
    window.removeEventListener("resize", this._updateDimensions);
  }
};
